import React, { useEffect } from 'react';
import { DrawerCustomClean } from '../drawer/DrawerCustomClean';
import { ArrowRightStartOnRectangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { MenuItem } from './MenuItem';
import { onlyInvoice, onlyRequests, userMenu } from '../../static/data';
import { useDispatch, useSelector } from 'react-redux';
import { logoutUser } from '../../redux/actions/user';
import { getTotalRequestInvoicePending, getTotalRequestStudentsPending } from '../../redux/actions/analytics';
import { AvatarWithInitials } from '../avatar/AvatarWithInitials';

export const DrawerMenu = ({
    isOpenDrawerMenu,
    toggleDrawerFilters,
    onOpenProfile,
    onOpenPaymentDrawer,
}) => {

    const dispatch = useDispatch();

    const { user, loading: loadingUser } = useSelector((state) => state.user);
    const { firstName, secondName, lastName, secondSurname, typeUser, status, matricula } = user;

    useEffect(() => {
        onlyRequests.includes(typeUser) &&
            dispatch(getTotalRequestStudentsPending('pending'));
    }, [dispatch, typeUser]);

    useEffect(() => {
        onlyInvoice.includes(typeUser) &&
            dispatch(getTotalRequestInvoicePending('pending'));
    }, [dispatch, typeUser]);

    const handleLogOut = () => {
        dispatch(logoutUser())
    }

    // Función para abrir el perfil del usuario loggeado
    const handleOpenProfile = () => {
        toggleDrawerFilters();
        if (onOpenProfile) {
            setTimeout(() => onOpenProfile(), 300);
        }
    }

    // Función para abrir el drawer de subir comprobante de pago
    const handleOpenPaymentDrawer = () => {
        toggleDrawerFilters();
        if (onOpenPaymentDrawer) {
            setTimeout(() => onOpenPaymentDrawer(), 300);
        }
    }

    return (
        <DrawerCustomClean
            isOpen={isOpenDrawerMenu}
            toggleDrawer={toggleDrawerFilters}
            direction='right'
        >
            <div className="flex flex-col h-full overflow-hidden">
                {/* Header Fijo - No hace scroll */}
                <div className='w-full bg-white rounded-lg border border-gray-200 flex-shrink-0 mb-4'>
                    {/* Header con gradiente */}
                    <div className='bg-gradient-to-r from-indigo-500 to-purple-600 p-6 rounded-t-lg'>
                        <div className='flex items-center space-x-4'>
                            {/* Avatar con iniciales si no hay imagen */}
                            <div className='relative flex-shrink-0'>
                                <AvatarWithInitials
                                    firstName={firstName}
                                    lastName={lastName}
                                    loading={loadingUser}
                                    size="lg"
                                />
                            </div>

                            {/* Información principal */}
                            <div className='flex-1 min-w-0'>
                                {loadingUser ? (
                                    <>
                                        {/* Skeleton mejorado para nombre con shimmer effect */}
                                        <div className='flex items-center gap-2 mb-2 flex-wrap'>
                                            <div className='h-8 w-64 bg-white/30 rounded-lg animate-pulse relative overflow-hidden'>
                                                <div className='absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer'></div>
                                            </div>
                                            <div className='h-6 w-16 bg-white/20 rounded-full animate-pulse relative overflow-hidden'>
                                                <div className='absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer'></div>
                                            </div>
                                        </div>
                                        {/* Skeleton para badges de info */}
                                        <div className='flex flex-wrap gap-2 mt-2'>
                                            <div className='h-8 w-32 bg-white/20 rounded-lg animate-pulse relative overflow-hidden'>
                                                <div className='absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer'></div>
                                            </div>
                                            <div className='h-8 w-28 bg-white/20 rounded-lg animate-pulse relative overflow-hidden'>
                                                <div className='absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer'></div>
                                            </div>
                                        </div>
                                        {/* Indicador de carga adicional */}
                                        <div className='flex items-center gap-2 mt-2'>
                                            <svg className="w-4 h-4 text-white/60 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <span className='text-white/60 text-xs font-medium'>Cargando información...</span>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className='flex items-center gap-2 mb-2 flex-wrap'>
                                            {/* Nombre con loading */}
                                            {!firstName && !lastName ? (
                                                <div className='h-8 w-64 bg-white/30 rounded-lg animate-pulse relative overflow-hidden'>
                                                    <div className='absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer'></div>
                                                </div>
                                            ) : (
                                                <h3 className='text-white font-bold text-xl lg:text-2xl capitalize tracking-tight truncate'>
                                                    {`${firstName} ${secondName ? secondName : ''} ${lastName} ${secondSurname ? secondSurname : ''}`.trim() || 'Usuario'}
                                                </h3>
                                            )}
                                            
                                            {/* Badge de estado - solo en desktop */}
                                            {!status ? (
                                                <div className='h-6 w-16 bg-white/20 rounded-full animate-pulse relative overflow-hidden'>
                                                    <div className='absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer'></div>
                                                </div>
                                            ) : (
                                                <>
                                                    {status === 'activo' && (
                                                        <span className='inline-flex items-center gap-1.5 bg-emerald-400/20 backdrop-blur-sm text-emerald-100 text-xs font-bold px-3 py-1 rounded-full border border-emerald-300/30 shadow-md'>
                                                            <span className='relative flex h-2 w-2'>
                                                                <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-75'></span>
                                                                <span className='relative inline-flex rounded-full h-2 w-2 bg-emerald-300'></span>
                                                            </span>
                                                            Activo
                                                        </span>
                                                    )}
                                                    {status === 'inactivo' && (
                                                        <span className='inline-flex items-center gap-1.5 bg-gray-400/20 backdrop-blur-sm text-gray-100 text-xs font-bold px-3 py-1 rounded-full border border-gray-300/30 shadow-md'>
                                                            <span className='relative flex h-2 w-2'>
                                                                <span className='relative inline-flex rounded-full h-2 w-2 bg-gray-300'></span>
                                                            </span>
                                                            Inactivo
                                                        </span>
                                                    )}
                                                    {status === 'baja' && (
                                                        <span className='inline-flex items-center gap-1.5 bg-red-400/20 backdrop-blur-sm text-red-100 text-xs font-bold px-3 py-1 rounded-full border border-red-300/30 shadow-md'>
                                                            <span className='relative flex h-2 w-2'>
                                                                <span className='relative inline-flex rounded-full h-2 w-2 bg-red-300'></span>
                                                            </span>
                                                            Baja
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        {/* Info badges */}
                                        <div className='flex flex-wrap gap-2 mt-2'>
                                            {/* Badge de matrícula con loading */}
                                            {!matricula ? (
                                                <div className='h-8 w-32 bg-white/20 rounded-lg animate-pulse relative overflow-hidden'>
                                                    <div className='absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer'></div>
                                                </div>
                                            ) : (
                                                <div className='inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-lg border border-white/30'>
                                                    <svg className="w-4 h-4 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                                                    </svg>
                                                    <span className='text-white text-sm font-semibold'>
                                                        {matricula}
                                                    </span>
                                                </div>
                                            )}

                                            {/* Badge de tipo de usuario con loading */}
                                            {!typeUser ? (
                                                <div className='h-8 w-28 bg-white/20 rounded-lg animate-pulse relative overflow-hidden'>
                                                    <div className='absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer'></div>
                                                </div>
                                            ) : (
                                                <div className='inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-lg border border-white/30'>
                                                    <svg className="w-4 h-4 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                    </svg>
                                                    <span className='text-white text-sm font-semibold capitalize'>
                                                        {typeUser}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content - Con scroll si hay overflow */}
                <div className='flex-1 overflow-auto py-4 px-4'>
                    {userMenu.map((item) => (
                        item.access.includes(user.typeUser) && (
                            (user.roles === 'user' && item.name === 'Configuracion')
                                ? null
                                : (
                                    <MenuItem
                                        toggleDrawerFilters={toggleDrawerFilters}
                                        key={item.name}
                                        name={item.name}
                                        description={item.description}
                                        href={item.href}
                                        icon={item.icon}
                                        onProfileClick={item.href === 'mi-perfil' ? handleOpenProfile : null}
                                        onCustomClick={item.openPaymentDrawer ? handleOpenPaymentDrawer : null}
                                    />
                                )
                        )
                    ))}
                </div>

                {/* Footer Fijo - Botón de logout mejorado */}
                <footer className="flex-shrink-0 w-full border-t-2 rounded-b-lg border-gray-100 bg-gradient-to-t from-gray-50 to-white p-4">
                    <button
                        type="button"
                        onClick={toggleDrawerFilters}
                        className="group relative mb-3 w-full md:hidden flex items-center gap-2 rounded-xl px-3 py-2 bg-gradient-to-r from-slate-50 to-gray-50 border-2 border-slate-200 hover:border-indigo-400 hover:from-indigo-500 hover:to-purple-600 cursor-pointer transition-all duration-300 ease-in-out hover:shadow-lg hover:scale-[1.02] active:scale-95"
                    >
                        <div className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-white group-hover:bg-indigo-100 transition-all duration-300 shadow-sm group-hover:shadow-md">
                            <XMarkIcon
                                aria-hidden="true"
                                className="h-4 w-4 text-slate-600 group-hover:text-indigo-700 transition-all duration-300"
                            />
                        </div>
                        <div className="flex-1 text-left">
                            <span className="block text-sm font-bold text-slate-700 group-hover:text-white transition-colors duration-300">
                                Cerrar Menú
                            </span>
                            <span className="hidden md:block text-xs text-slate-500 group-hover:text-indigo-100 transition-colors duration-300 mt-0.5">
                                Volver a la pantalla anterior
                            </span>
                        </div>
                        <div className="flex items-center opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1">
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </div>
                        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transition-opacity duration-500 group-hover:animate-shimmer"></div>
                    </button>
                    <button
                        onClick={() => handleLogOut()}
                        className="group relative w-full flex items-center gap-2 md:gap-4 rounded-xl px-3 py-2 md:p-4 bg-gradient-to-r from-slate-50 to-gray-50 hover:from-indigo-500 hover:to-purple-600 border-2 border-slate-200 hover:border-indigo-400 cursor-pointer transition-all duration-300 ease-in-out hover:shadow-lg hover:scale-[1.02] active:scale-95"
                    >
                        {/* Icono animado */}
                        <div className="flex h-8 w-8 md:h-12 md:w-12 flex-none items-center justify-center rounded-lg md:rounded-xl bg-white group-hover:bg-indigo-100 transition-all duration-300 shadow-sm group-hover:shadow-md">
                            <ArrowRightStartOnRectangleIcon
                                aria-hidden="true"
                                className="h-4 w-4 md:h-6 md:w-6 text-slate-600 group-hover:text-indigo-700 transition-all duration-300"
                            />
                        </div>

                        {/* Texto */}
                        <div className="flex-1 text-left">
                            <span className="block text-sm md:text-base font-bold text-slate-700 group-hover:text-white transition-colors duration-300">
                                Cerrar Sesión
                            </span>
                            <span className="hidden md:block text-xs text-slate-500 group-hover:text-indigo-100 transition-colors duration-300 mt-0.5">
                                Salir de la plataforma
                            </span>
                        </div>

                        {/* Flecha indicadora */}
                        <div className="flex items-center opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1">
                            <svg className="w-4 h-4 md:w-5 md:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </div>

                        {/* Efecto de brillo en hover */}
                        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transition-opacity duration-500 group-hover:animate-shimmer"></div>
                    </button>
                </footer>
            </div>
        </DrawerCustomClean>
    )
}
